import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusCell } from "@/components/StatusCell";
import { RiskBadge } from "@/components/RiskBadge";
import { relativeDays, CHANNEL_LABEL } from "@/lib/format";
import { Checkbox } from "@/components/ui/checkbox";
import { Send, SlidersHorizontal, Search, FolderCheck, Columns3, Bell, Trash2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STORAGE_KEY = "matrix-visible-cols";

function ProgressBar({ value }) {
  const color = value >= 100 ? "bg-emerald-500" : value >= 60 ? "bg-blue-500" : value >= 30 ? "bg-amber-500" : "bg-red-400";
  return (
    <div className="flex items-center gap-1.5 min-w-[88px]">
      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs font-semibold text-slate-600 w-8 text-right">{value}%</span>
    </div>
  );
}

function DocBadge({ uploaded, total, progress }) {
  const done = total > 0 && uploaded === total;
  const cls = done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : progress > 0 ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-50 text-slate-500 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      <FolderCheck size={12} /> {uploaded}/{total}
    </span>
  );
}

export default function MatrixTable({ requirements, workers, projects, positions = [], mandantes = [], onReload, onOpenWorker, onSend, selected, setSelected, canEdit, search, setSearch }) {
  const [mandante, setMandante] = useState("all");
  const [position, setPosition] = useState("all");
  const [risk, setRisk] = useState("all");
  const [intent, setIntent] = useState("all");
  const [onlyPending, setOnlyPending] = useState(false);
  const [visibleCols, setVisibleCols] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // worker | "bulk" | null
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (Array.isArray(saved)) setVisibleCols(saved);
      else setVisibleCols(requirements.map((r) => r.code));
    } catch { setVisibleCols(requirements.map((r) => r.code)); }
  }, [requirements.length]);

  const shownReqs = useMemo(() => {
    if (!visibleCols) return requirements;
    const sel = requirements.filter((r) => visibleCols.includes(r.code));
    return sel.length ? sel : requirements;
  }, [requirements, visibleCols]);

  const toggleCol = (code) => {
    const base = visibleCols || requirements.map((r) => r.code);
    const next = base.includes(code) ? base.filter((c) => c !== code) : [...base, code];
    setVisibleCols(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const filtered = useMemo(() => {
    return workers.filter((w) => {
      const wMandante = w.mandante || w.company || "";
      if (mandante !== "all" && wMandante !== mandante) return false;
      if (position !== "all" && w.position !== position) return false;
      if (risk !== "all" && w.risk_level !== risk) return false;
      if (intent !== "all" && w.intent_status !== intent) return false;
      if (onlyPending && w.progress_percentage >= 100) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = `${w.first_name} ${w.last_name} ${w.rut} ${w.position} ${wMandante} ${w.contrato || ""}`.toLowerCase();
        // RUT-flexible: ignore dots and dashes on both sides
        const sNoFmt = s.replace(/[.\-\s]/g, "");
        const hayNoFmt = hay.replace(/[.\-\s]/g, "");
        if (!hay.includes(s) && !hayNoFmt.includes(sNoFmt)) return false;
      }
      return true;
    });
  }, [workers, mandante, position, risk, intent, onlyPending, search]);

  // Pagination: reset to page 1 whenever the filtered set changes
  useEffect(() => { setPage(1); }, [mandante, position, risk, intent, onlyPending, search, workers.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage]
  );
  const rangeStart = filtered.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filtered.length);

  const allSelected = filtered.length > 0 && filtered.every((w) => selected.includes(w.id));
  const toggleAll = () => {
    if (allSelected) setSelected(selected.filter((id) => !filtered.some((w) => w.id === id)));
    else setSelected([...new Set([...selected, ...filtered.map((w) => w.id)])]);
  };
  const toggleOne = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const reqStatus = (w, code) => {
    const r = w.requirements.find((r) => r.code === code);
    return r ? r.status : null; // null => no aplica al mandante del worker
  };

  const updateStatus = async (w, code, status) => {
    try { await api.patch(`/workers/${w.id}/requirements/${code}`, { status }); toast.success("Estado actualizado"); onReload(); }
    catch (e) { toast.error("No se pudo actualizar"); }
  };

  const remindByDocument = (req) => {
    const ids = filtered.filter((w) => {
      const st = reqStatus(w, req.code);
      return st !== null && st !== "COMPLETED";
    }).map((w) => w.id);
    if (!ids.length) { toast.info(`Nadie tiene pendiente: ${req.name}`); return; }
    toast.success(`${ids.length} trabajador(es) sin ${req.name}`);
    onSend(ids);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget === "bulk") {
        const { data } = await api.post("/workers/bulk-delete", { worker_ids: selected });
        toast.success(`${data.deleted} trabajador(es) eliminados`);
        setSelected([]);
      } else {
        await api.delete(`/workers/${deleteTarget.id}`);
        toast.success("Trabajador eliminado");
        setSelected(selected.filter((id) => id !== deleteTarget.id));
      }
      onReload();
    } catch (e) {
      toast.error("No se pudo eliminar");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const colCount = shownReqs.length + (canEdit ? 8 : 6);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-slate-500 mr-1"><SlidersHorizontal size={15} /><span className="text-sm font-medium">Filtros</span></div>
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="matrix-search-input" value={search || ""} onChange={(e) => setSearch && setSearch(e.target.value)} placeholder="Buscar nombre o RUT..."
                className="h-9 w-[180px] pl-8 pr-3 rounded-md bg-white border border-slate-200 focus:border-blue-400 outline-none text-sm" />
            </div>
            <Select value={mandante} onValueChange={setMandante}>
              <SelectTrigger data-testid="filter-mandante" className="h-9 min-w-[200px] w-auto bg-white text-sm"><SelectValue placeholder="Mandante" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">Todos los mandantes</SelectItem>{mandantes.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={position} onValueChange={setPosition}>
              <SelectTrigger data-testid="filter-position" className="h-9 min-w-[170px] w-auto bg-white text-sm"><SelectValue placeholder="Cargo" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">Todos los cargos</SelectItem>{positions.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={risk} onValueChange={setRisk}>
              <SelectTrigger data-testid="filter-risk" className="h-9 min-w-[140px] w-auto bg-white text-sm"><SelectValue placeholder="Riesgo" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">Todo riesgo</SelectItem><SelectItem value="LOW">Bajo</SelectItem><SelectItem value="MEDIUM">Medio</SelectItem><SelectItem value="HIGH">Alto</SelectItem><SelectItem value="CRITICAL">Crítico</SelectItem></SelectContent>
            </Select>
            <Select value={intent} onValueChange={setIntent}>
              <SelectTrigger data-testid="filter-intent" className="h-9 min-w-[170px] w-auto bg-white text-sm"><SelectValue placeholder="Intención" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">Toda intención</SelectItem><SelectItem value="NONE">Sin respuesta</SelectItem><SelectItem value="CONTINUE">Quiere continuar</SelectItem><SelectItem value="NEEDS_HELP">Necesita ayuda</SelectItem><SelectItem value="WITHDRAW">No continuará</SelectItem></SelectContent>
            </Select>
            <button data-testid="filter-pending-toggle" onClick={() => setOnlyPending((v) => !v)}
              className={`h-9 px-3 rounded-md text-sm font-medium border transition-colors ${onlyPending ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              Avance &lt; 100%
            </button>

            {/* Column config */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="columns-config-button" className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
                  <Columns3 size={15} /> Columnas
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white w-60">
                <DropdownMenuLabel className="text-xs text-slate-500">Documentos visibles en la planilla</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {requirements.map((r) => (
                  <DropdownMenuCheckboxItem key={r.code} data-testid={`col-toggle-${r.code}`}
                    checked={(visibleCols || requirements.map((x) => x.code)).includes(r.code)}
                    onCheckedChange={() => toggleCol(r.code)} onSelect={(e) => e.preventDefault()}>
                    {r.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {canEdit && (
            <div className="flex items-center gap-2">
              {/* Reminder by document */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="remind-by-document-button" className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
                    <Bell size={15} /> Recordar documento
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white w-64">
                  <DropdownMenuLabel className="text-xs text-slate-500">Enviar a quienes les falta…</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {requirements.map((r) => (
                    <DropdownMenuItem key={r.code} data-testid={`remind-doc-${r.code}`} className="cursor-pointer" onClick={() => remindByDocument(r)}>
                      {r.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {selected.length > 0 && (
                <span data-testid="selected-count-pill" className="text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">{selected.length} sel.</span>
              )}
              {selected.length > 0 && (
                <button data-testid="bulk-delete-button" onClick={() => setDeleteTarget("bulk")}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium border border-red-200 bg-white text-red-600 hover:bg-red-50">
                  <Trash2 size={15} /> Eliminar
                </button>
              )}
              <button data-testid="send-reminder-selected-button" disabled={selected.length === 0} onClick={() => onSend(selected)}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2.5 rounded-lg shadow-sm transition-colors">
                <Send size={16} /> Enviar recordatorio{selected.length > 0 ? ` a ${selected.length}` : ""}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto matrix-scroll">
        <table data-testid="tracking-matrix-table" className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-100">
              {canEdit && <th className="px-3 py-3 w-9"><Checkbox data-testid="matrix-select-all-checkbox" checked={allSelected} onCheckedChange={toggleAll} /></th>}
              <th className="px-2 py-3 text-left whitespace-nowrap">RUT</th>
              <th className="px-3 py-3 text-left">Trabajador</th>
              <th className="px-2 py-3 text-left">Mandante / Contrato</th>
              {shownReqs.map((r) => (
                <th key={r.code} className="px-1 py-3 text-center"><div className="mx-auto w-[78px] leading-[1.15] text-[10px] break-normal whitespace-normal normal-case font-semibold text-slate-500">{r.name}</div></th>
              ))}
              <th className="px-2 py-3 text-center whitespace-nowrap">Docs.</th>
              <th className="px-3 py-3 text-left">Avance</th>
              <th className="px-2 py-3 text-left whitespace-nowrap">Contacto</th>
              <th className="px-2 py-3 text-left">Riesgo</th>
              {canEdit && <th className="px-2 py-3 w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {paginated.map((w) => (
              <tr key={w.id} data-testid={`matrix-worker-row-${w.id}`} className={`group border-b border-slate-50 hover:bg-slate-50/60 transition-colors ${selected.includes(w.id) ? "bg-blue-50/40" : ""}`}>
                {canEdit && <td className="px-3 py-3"><Checkbox data-testid={`row-checkbox-${w.id}`} checked={selected.includes(w.id)} onCheckedChange={() => toggleOne(w.id)} /></td>}
                <td className="px-2 py-3 font-mono text-[11px] text-slate-600 whitespace-nowrap" data-testid={`worker-rut-${w.id}`}>{w.rut || "—"}</td>
                <td className="px-3 py-3">
                  <button onClick={() => onOpenWorker(w.id)} data-testid={`open-worker-${w.id}`} className="text-left group leading-tight">
                    <p className="font-semibold text-slate-800 group-hover:text-blue-600">{w.first_name} {w.last_name}</p>
                    <p className="text-xs text-slate-400">{w.position || "—"}</p>
                  </button>
                </td>
                <td className="px-2 py-3 text-slate-600 text-xs leading-tight">
                  {(w.mandante || w.company) ? (
                    <>
                      <p>{w.mandante || w.company}</p>
                      {w.contrato && <p className="text-[11px] text-slate-400">{w.contrato}</p>}
                    </>
                  ) : (
                    <span data-testid={`unassigned-${w.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                      Sin asignación
                    </span>
                  )}
                </td>
                {shownReqs.map((r) => {
                  const st = reqStatus(w, r.code);
                  return (
                    <td key={r.code} className="px-1 py-3 text-center">
                      {st === null
                        ? <span data-testid={`na-${w.id}-${r.code}`} className="text-slate-300 text-xs" title="No aplica a este mandante">—</span>
                        : <StatusCell compact testid={`status-${w.id}-${r.code}`} status={st} readOnly={!canEdit} onChange={(s) => updateStatus(w, r.code, s)} />}
                    </td>
                  );
                })}
                <td className="px-2 py-3 text-center">
                  <button onClick={() => onOpenWorker(w.id)} data-testid={`doc-badge-${w.id}`}>
                    <DocBadge uploaded={w.documents_uploaded ?? 0} total={w.documents_total ?? 0} progress={w.documents_progress ?? 0} />
                  </button>
                </td>
                <td className="px-3 py-3"><ProgressBar value={w.progress_percentage} /></td>
                <td className="px-2 py-3 text-slate-500 whitespace-nowrap text-xs">
                  {relativeDays(w.last_contact_at)}
                  {w.last_contact_channel && <span className="block text-[11px] text-slate-400">{CHANNEL_LABEL[w.last_contact_channel] || w.last_contact_channel}</span>}
                </td>
                <td className="px-2 py-3"><RiskBadge level={w.risk_level} testid={`risk-badge-${w.id}`} /></td>
                {canEdit && (
                  <td className="px-2 py-3 text-right">
                    <button data-testid={`delete-worker-${w.id}`} onClick={(e) => { e.stopPropagation(); setDeleteTarget(w); }}
                      className="opacity-0 group-hover:opacity-100 inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-opacity" title="Eliminar trabajador">
                      <Trash2 size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={colCount} className="px-4 py-16 text-center text-slate-400">No hay trabajadores que coincidan con los filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filtered.length > 0 && (
        <div data-testid="matrix-pagination" className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-white">
          <p className="text-xs text-slate-500">
            Mostrando <span className="font-semibold text-slate-700">{rangeStart}–{rangeEnd}</span> de <span className="font-semibold text-slate-700">{filtered.length}</span>
          </p>
          <div className="flex items-center gap-1">
            <button data-testid="pagination-prev" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}
              className="h-8 px-3 rounded-md text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
              Anterior
            </button>
            <span className="text-xs text-slate-500 px-2">Página <span className="font-semibold text-slate-700">{safePage}</span> de <span className="font-semibold text-slate-700">{totalPages}</span></span>
            <button data-testid="pagination-next" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}
              className="h-8 px-3 rounded-md text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
              Siguiente
            </button>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget === "bulk"
                ? `Eliminar ${selected.length} trabajadores`
                : `Eliminar ${deleteTarget?.first_name || ""} ${deleteTarget?.last_name || ""}`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción es irreversible. Se eliminará el trabajador, sus documentos, requisitos, alertas y timeline asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" disabled={deleting} onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700">
              {deleting ? "Eliminando…" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
